import { AREAS } from './site';
import leakPhoto from '../assets/dde.webp';

const TYPES_DE_FUITE = [
  "[Canalisations d'eau encastrées ou enterrées](/recherche-fuite-canalisation) : dans une dalle, un mur, sous le jardin.",
  '[Chauffage et chauffage au sol](/recherche-fuite-chauffage) : chaudière qui perd de la pression, radiateurs, boucles de plancher chauffant.',
  "[Évacuations et égouts](/recherche-fuite-evacuation) : descentes, raccordements, réseaux enterrés, odeurs d'égout.",
  '[Douches, baignoires et fuites sous carrelage](/recherche-fuite-douche) : alimentation, évacuation ou étanchéité.',
  "[Infiltrations d'eau](/recherche-infiltration) : toiture, terrasse, façade, cave.",
  "[Surconsommation d'eau](/surconsommation-eau) : compteur qui tourne, facture anormale, fuite sans trace visible.",
];

const cityFaq = (ville) => [
  {
    q: `Faut-il casser pour trouver une fuite à ${ville} ?`,
    a: "Non. Nous localisons la fuite avec des techniques non destructives. Il faudra ensuite ouvrir pour réparer, mais seulement à l'endroit que nous avons marqué, et non au hasard.",
  },
  {
    q: 'Mon assurance belge acceptera-t-elle le rapport d’une société basée au Luxembourg ?',
    a: "Notre rapport s'adresse à votre assureur, qu'il soit belge ou luxembourgeois : il décrit les constats, les techniques utilisées, l'origine de la fuite et les dommages observés. Les conditions de prise en charge dépendent de votre contrat ; vérifiez-les ou interrogez votre courtier.",
  },
  {
    q: 'Sous quel délai recevrai-je le rapport ?',
    a: 'Le rapport complet, illustré de photos et de mesures, vous est remis sous 72h.',
  },
];

export const zones = [
  {
    path: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite au Luxembourg',
    summary: "Localisation non destructive des fuites d'eau et infiltrations dans tout le Grand-Duché, rapport sous 72h.",
    title: 'Recherche de fuite Luxembourg, non destructive | Expomis',
    description: "Recherche de fuite d'eau non destructive dans tout le Grand-Duché : canalisations, chauffage, évacuations, infiltrations. Rapport pour l'assurance sous 72h.",
    eyebrow: 'Grand-Duché de Luxembourg',
    h1: 'Recherche de fuite au Luxembourg',
    image: leakPhoto,
    imageAlt: "Fuite d'eau sur une canalisation en cuivre",
    areaServed: AREAS.lu,
    intro: [
      "Expomis localise les fuites d'eau sans démolition dans tout le Grand-Duché de Luxembourg, depuis ses bureaux de Steinfort. Canalisation encastrée, chauffage au sol, évacuation, douche, infiltration par la toiture ou la façade : nous trouvons l'origine de la fuite, nous la situons précisément et nous vous remettons un rapport sous 72h, utilisable auprès de votre assurance.",
      "Nous sommes aussi experts en sinistres. Au-delà de la fuite elle-même, nous savons ce dont votre assureur a besoin pour instruire un dossier de [dégât des eaux](/degat-des-eaux).",
    ],
    sections: [
      {
        h2: 'Quand faire appel à une recherche de fuite ?',
        body: ['Une fuite cachée se remarque rarement directement. On en voit les effets :'],
        list: [
          'une tache d’humidité qui s’étend au plafond ou sur un mur ;',
          'un compteur d’eau qui tourne alors que tout est fermé, ou une facture anormalement élevée ;',
          'une chaudière qui perd de la pression et qu’il faut recharger régulièrement ;',
          'une peinture qui cloque, des joints qui noircissent, une odeur de moisi ;',
          'un carrelage qui se décolle, un parquet qui gonfle ;',
          "de l'eau qui apparaît chez le voisin du dessous.",
        ],
        after: [
          'Plus une fuite dure, plus les dégâts s’étendent : matériaux imbibés, moisissures, parfois atteinte de la structure. La localiser vite limite aussi le coût des réparations.',
        ],
      },
      {
        h2: 'Recherche de fuite non destructive : ce que cela veut dire',
        body: [
          "Non destructive ne veut pas dire qu'aucune ouverture ne sera nécessaire : la canalisation devra bien être réparée. Cela veut dire que la fuite est localisée sans ouvrir au hasard. Au lieu de casser un mur entier ou de refaire toute une salle de bain, on n'ouvre qu'à l'endroit que nous avons marqué, sur une surface limitée.",
        ],
      },
      {
        h2: 'Les fuites que nous recherchons',
        list: TYPES_DE_FUITE,
      },
      {
        h2: 'Nos techniques de détection',
        body: [
          "Aucune technique ne fonctionne dans tous les cas. Le choix dépend du réseau (eau froide, eau chaude, chauffage, évacuation), de sa pression et des matériaux qui le recouvrent. Nous combinons souvent plusieurs méthodes pour recouper les résultats. [Voir toutes nos techniques](/techniques).",
        ],
        list: [
          '[Mesure d’humidité](/techniques/humidimetre) pour délimiter la zone touchée ;',
          '[Contrôle de pression](/techniques/mise-en-pression) pour confirmer la fuite et identifier le circuit en cause ;',
          '[Caméra thermique](/techniques/camera-thermique) pour l’eau chaude et le chauffage au sol ;',
          '[Détection acoustique](/techniques/detection-acoustique) pour les canalisations sous pression ;',
          '[Gaz traceur](/techniques/gaz-traceur) pour les fuites faibles, enterrées ou sous dalle ;',
          '[Fluorescéine](/techniques/fluoresceine), [test fumigène](/techniques/test-fumigene) et [caméra d’inspection](/techniques/camera-canalisation) pour les évacuations ;',
          '[Endoscopie](/techniques/endoscopie) pour voir dans les zones inaccessibles.',
        ],
      },
      {
        h2: 'Comment se déroule l’intervention',
        ordered: true,
        list: [
          'Vous nous décrivez les symptômes par téléphone ou via le formulaire, sans engagement.',
          'Sur place, nous observons, photographions et mesurons l’humidité pour délimiter la zone touchée.',
          'Nous testons les circuits en pression pour savoir lequel fuit, puis nous localisons le point de fuite avec la technique adaptée.',
          "L'emplacement de la fuite est marqué sur place.",
          'Vous recevez sous 72h un [rapport complet](/rapport-recherche-fuite), illustré de photos et de mesures.',
        ],
      },
      {
        h2: 'Recherche de fuite et assurance',
        body: [
          "Au Luxembourg, de nombreux contrats d'assurance habitation prennent en charge les frais de recherche de fuite lorsqu'elle est à l'origine d'un dégât des eaux. Les conditions varient d'un contrat à l'autre : vérifiez les vôtres ou interrogez votre agent.",
          "Notre rapport décrit les constats, les techniques utilisées, l'origine identifiée et les dommages observés : c'est la pièce que l'assureur attend pour instruire le dossier. Et si vous estimez que l'évaluation de l'expert de votre assureur ne reflète pas la réalité, nous pouvons intervenir comme [contre-expert indépendant](/contre-expertise).",
        ],
      },
      {
        h2: 'Où intervenons-nous ?',
        body: [
          "Dans l'ensemble du Grand-Duché : [Luxembourg-Ville](/recherche-fuite-luxembourg-ville), [Esch-sur-Alzette](/recherche-fuite-esch-sur-alzette), [Differdange](/recherche-fuite-differdange), [Dudelange](/recherche-fuite-dudelange), [Pétange](/recherche-fuite-petange), [Sanem](/recherche-fuite-sanem), [Mamer](/recherche-fuite-mamer), [Strassen](/recherche-fuite-strassen), [Bertrange](/recherche-fuite-bertrange), [Hesperange](/recherche-fuite-hesperange), [Capellen](/recherche-fuite-mamer), [Mersch](/recherche-fuite-mersch), [Ettelbruck](/recherche-fuite-ettelbruck), [Diekirch](/recherche-fuite-diekirch), [Wiltz](/recherche-fuite-wiltz), [Grevenmacher](/recherche-fuite-grevenmacher), [Remich](/recherche-fuite-remich), [Echternach](/recherche-fuite-echternach), [Steinfort](/recherche-fuite-steinfort) et toutes les autres communes.",
          'Basés à [Steinfort](/recherche-fuite-steinfort), à la frontière belge, nous intervenons aussi régulièrement dans le [sud de la province de Luxembourg, en Belgique](/recherche-fuite-province-luxembourg).',
        ],
      },
    ],
    faq: [
      {
        q: 'Combien coûte une recherche de fuite au Luxembourg ?',
        a: "Chaque recherche de fuite est facturée à la prestation, à un prix fixé avec vous avant l'intervention ; si la fuite est trouvée en une seule visite sans démontage, un forfait s'applique lorsqu'il est plus avantageux. Le prix dépend du type de fuite, de l'étendue du réseau à contrôler et des techniques nécessaires. Dans de nombreux cas, les frais sont pris en charge par l'assurance habitation. Voir [prix d'une recherche de fuite](/prix-recherche-fuite).",
      },
      {
        q: 'Faut-il casser pour trouver une fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais uniquement à l'endroit précis de la fuite.",
      },
      {
        q: 'Que faire en attendant votre intervention ?',
        a: "Si la fuite est importante, fermez l'arrivée d'eau générale. Coupez l'électricité des pièces touchées si l'eau approche des installations électriques. Photographiez les dégâts et prévenez votre assureur. Évitez de démonter ou de modifier l'installation : cela complique la localisation.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg-ville', '/recherche-fuite-province-luxembourg', '/rapport-recherche-fuite'],
  },
  {
    path: '/recherche-fuite-luxembourg-ville',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Luxembourg-Ville',
    summary: 'Appartements en copropriété, maisons de ville et immeubles anciens, de la Ville-Haute au Kirchberg.',
    title: 'Recherche de fuite à Luxembourg-Ville | Sans casse | Expomis',
    description: 'Recherche de fuite non destructive à Luxembourg-Ville : copropriétés, fuite chez le voisin, parkings souterrains, immeubles anciens. Rapport sous 72h.',
    eyebrow: 'Luxembourg-Ville',
    h1: "Recherche de fuite d'eau à Luxembourg-Ville",
    areaServed: [{ '@type': 'City', name: 'Luxembourg' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans tous les quartiers de Luxembourg-Ville : Ville-Haute, Gare, Bonnevoie, Limpertsberg, Belair, Merl, Hollerich, Gasperich, Cessange, Kirchberg, Neudorf, Cents, Clausen, Grund, Pfaffenthal et les autres.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite : c'est la pièce que demandent l'assureur, le syndic ou le propriétaire.",
    ],
    sections: [
      {
        h2: 'En appartement : de quel logement vient la fuite ?',
        body: [
          "À Luxembourg-Ville, une grande partie des logements sont des appartements en copropriété. La fuite apparaît souvent au plafond du voisin du dessous, et la première question n'est pas technique : qui est concerné ? Le voisin du dessus, la colonne commune, la toiture-terrasse ?",
          "Nous isolons et testons en pression chaque circuit du logement suspect, ce qui établit s'il fuit ou non. Nous localisons ensuite le point de fuite. Le rapport indique si l'origine se trouve dans une partie privative ou dans une partie commune de l'immeuble, information dont le syndic et les assureurs ont besoin pour savoir qui prend le sinistre en charge.",
        ],
      },
      {
        h2: 'Résidences récentes : chauffage au sol, gaines techniques, parkings',
        body: [
          "Dans les résidences construites ces dernières décennies, au Kirchberg, à Gasperich ou à la Cloche d'Or comme ailleurs en ville, plusieurs situations reviennent :",
        ],
        list: [
          "le [chauffage au sol](/recherche-fuite-chauffage) qui perd de la pression, avec des boucles noyées dans la chape : nous testons chaque boucle au collecteur, puis la [caméra thermique](/techniques/camera-thermique) fait apparaître la fuite ;",
          "les canalisations dans les gaines techniques et les faux plafonds, que l'[endoscopie](/techniques/endoscopie) permet d'inspecter par une petite ouverture ;",
          "les infiltrations dans les parkings souterrains et les caves, par les murs enterrés, les joints de dalle ou la toiture-terrasse au-dessus : la [fluorescéine](/techniques/fluoresceine) relie l'eau qui coule à son point d'entrée.",
        ],
      },
      {
        h2: 'Immeubles anciens : préserver ce qui ne se remplace pas',
        body: [
          "Dans la Ville-Haute, à Limpertsberg, à Belair ou dans les maisons de ville de la Gare et de Bonnevoie, les installations ont souvent été modifiées au fil des rénovations. Des tronçons anciens en acier galvanisé, parfois en plomb, y côtoient du cuivre ou du multicouche, et les raccords entre ces matériaux sont des points de fuite fréquents.",
          "Ces immeubles ont aussi des parquets, des moulures et des carrelages anciens qu'on ne remplace pas à l'identique. C'est là que la recherche non destructive compte le plus : ouvrir au bon endroit, sur une surface limitée, plutôt que sonder un mur ou un sol entier.",
        ],
      },
      {
        h2: 'Dans les vallées : fuite ou humidité du terrain ?',
        body: [
          "Au Grund, à Clausen et au Pfaffenthal, les maisons sont construites au bord de l'Alzette, souvent adossées au rocher. Les murs de cave y sont humides pour des raisons qui n'ont rien à voir avec une fuite : eau du terrain, remontées capillaires. Avant d'engager des travaux, nous établissons d'où vient l'eau : cartographie à l'[humidimètre](/techniques/humidimetre), puis test des canalisations en pression. Si elles sont étanches, nous le disons, et le rapport l'établit.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Luxembourg-Ville',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "Je suis locataire et j'ai une fuite : qui doit faire appel à vous ?",
        a: "Prévenez d'abord votre propriétaire ou l'agence, ainsi que votre assureur. La recherche de fuite peut être commandée par le locataire, le propriétaire ou le syndic : le rapport, qui situe l'origine de la fuite, sert ensuite à déterminer qui prend la réparation en charge. Les règles précises dépendent de votre bail et des contrats d'assurance.",
      },
      {
        q: "L'eau vient de chez mon voisin du dessus, mais il n'est pas joignable. Que faire ?",
        a: "Prévenez le syndic : il peut accéder aux parties communes et contacter le copropriétaire. Pour localiser précisément la fuite, nous aurons besoin d'accéder au logement d'où elle provient. En attendant, photographiez les dégâts et déclarez le sinistre à votre assureur.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: "Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à l'assureur ou au syndic.",
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/degat-des-eaux', '/recherche-fuite-chauffage'],
  },
  {
    path: '/recherche-fuite-esch-sur-alzette',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Esch-sur-Alzette',
    summary: 'Maisons ouvrières mitoyennes, immeubles du centre avec commerces, résidences récentes de Belval.',
    title: 'Recherche de fuite à Esch-sur-Alzette | Sans casse | Expomis',
    description: "Recherche de fuite non destructive à Esch-sur-Alzette et Belval : maisons mitoyennes, logement au-dessus d'un commerce, résidences récentes. Rapport sous 72h.",
    eyebrow: 'Esch-sur-Alzette',
    h1: "Recherche de fuite d'eau à Esch-sur-Alzette",
    areaServed: [{ '@type': 'City', name: 'Esch-sur-Alzette' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans tous les quartiers d'Esch-sur-Alzette : centre-ville, Brill, Grenz, Lallange, Raemerich, Dellhéicht, Wobrecken, Belval et les autres.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur, à votre propriétaire ou au syndic.",
    ],
    sections: [
      {
        h2: 'Maisons mitoyennes : chez vous ou chez le voisin ?',
        body: [
          "Esch s'est construite autour de la sidérurgie, et de nombreux quartiers sont faits de maisons ouvrières mitoyennes, souvent centenaires. Leur installation a été reprise rénovation après rénovation : des tronçons anciens en acier galvanisé, parfois en plomb, y sont raccordés à du cuivre ou du multicouche. Ces raccords et les canalisations anciennes corrodées de l'intérieur sont des origines de fuite fréquentes.",
          "Dans ces maisons, une tache d'humidité sur le mur mitoyen pose une question délicate : l'eau vient-elle de chez vous ou de chez le voisin ? Nous testons vos circuits en pression : s'ils sont étanches, le rapport l'établit, et la recherche se poursuit de l'autre côté du mur avec l'accord du voisin.",
        ],
      },
      {
        h2: "Centre-ville : quand l'appartement fuit dans le commerce",
        body: [
          "Dans le centre, autour de la rue de l'Alzette, beaucoup d'immeubles ont un commerce au rez-de-chaussée et des appartements aux étages. Une fuite dans une salle de bain ou une cuisine se voit alors d'abord au plafond du magasin, avec du stock abîmé et parfois une fermeture forcée.",
          "Il faut aller vite et savoir précisément d'où vient l'eau. Nous testons les circuits de chaque logement au-dessus, localisons le point de fuite et le marquons sur place pour que la réparation puisse commencer. Le rapport décrit l'origine et les dommages constatés : il sert au commerçant comme au propriétaire de l'appartement face à leurs assureurs respectifs. Voir aussi notre page [dégât des eaux](/degat-des-eaux).",
        ],
      },
      {
        h2: 'Belval et les résidences récentes',
        body: [
          "Le quartier de Belval, construit sur l'ancien site sidérurgique, et les résidences récentes de Lallange ou de Raemerich ont des installations modernes, avec leurs propres fuites :",
        ],
        list: [
          "[chauffage au sol](/recherche-fuite-chauffage) qui perd de la pression, avec des boucles noyées dans la chape : test boucle par boucle au collecteur, puis [caméra thermique](/techniques/camera-thermique) ;",
          "[douches à l'italienne](/recherche-fuite-douche) dont l'étanchéité sous carrelage a cédé, avec de l'eau qui apparaît chez le voisin du dessous ;",
          "[infiltrations](/recherche-infiltration) par les toitures-terrasses, les terrasses d'étage ou dans les parkings souterrains, que la [fluorescéine](/techniques/fluoresceine) relie à leur point d'entrée.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Esch-sur-Alzette',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "Mon mur mitoyen est humide : comment savoir si la fuite est chez moi ?",
        a: "En testant vos circuits en pression (eau froide, eau chaude, chauffage). S'ils tiennent la pression, la fuite n'est pas chez vous, et notre rapport l'établit. L'humidité peut alors venir du voisin, d'une infiltration ou de remontées capillaires : la mesure d'humidité aide à trancher.",
      },
      {
        q: "Une fuite de l'appartement du dessus a inondé mon commerce. Que faire ?",
        a: "Coupez l'électricité de la zone touchée, mettez le stock à l'abri, photographiez les dégâts et déclarez le sinistre à votre assureur. Prévenez l'occupant du dessus et, s'il y en a un, le syndic. Appelez-nous pour localiser la fuite : nous aurons besoin d'accéder au logement d'où elle provient.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/degat-des-eaux', '/recherche-fuite-luxembourg-ville'],
  },
  {
    path: '/recherche-fuite-differdange',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Differdange',
    summary: 'Differdange, Oberkorn, Niederkorn, Fousbann et Lasauvage : résidences récentes et maisons anciennes.',
    title: 'Recherche de fuite à Differdange | Sans casse | Expomis',
    description: 'Recherche de fuite non destructive à Differdange, Oberkorn, Niederkorn et Lasauvage : logement neuf sous garantie, cave humide. Rapport sous 72h.',
    eyebrow: 'Differdange',
    h1: "Recherche de fuite d'eau à Differdange",
    areaServed: [{ '@type': 'City', name: 'Differdange' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans toute la commune de Differdange : Differdange, Oberkorn, Niederkorn, Fousbann et Lasauvage.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur, à votre propriétaire, au syndic ou au promoteur.",
    ],
    sections: [
      {
        h2: 'Logement récent : une fuite peut relever des garanties',
        body: [
          "De nombreuses résidences et maisons ont été construites à Differdange ces dernières années. Quand une fuite apparaît dans un logement récent, elle peut relever des garanties du promoteur ou des entreprises qui ont construit : garantie biennale ou décennale selon l'élément en cause.",
          "Pour faire valoir ces garanties, il faut savoir précisément ce qui fuit et pourquoi : un raccord mal serti, une boucle de [chauffage au sol](/recherche-fuite-chauffage) percée, une étanchéité de [douche](/recherche-fuite-douche) absente ou mal posée, une évacuation à contre-pente. Notre rapport décrit l'origine, photos et mesures à l'appui, avant que la réparation ne fasse disparaître les preuves. Il vous appartient ensuite, avec votre conseil, de faire valoir vos droits.",
        ],
      },
      {
        h2: 'Maisons à flanc de coteau : fuite ou infiltration ?',
        body: [
          "Dans les rues en pente d'Oberkorn, de Niederkorn ou de Differdange, beaucoup de maisons ont un niveau semi-enterré : une cave, un garage ou une pièce de vie adossée au terrain. Quand un mur de ce niveau est humide, l'eau peut venir d'une canalisation qui fuit, d'une évacuation enterrée, ou du terrain lui-même par un mur dont l'étanchéité a vieilli.",
          "Les réparations n'ont rien à voir : remplacer un tronçon de canalisation, ou refaire l'étanchéité extérieure d'un mur enterré. Nous commençons par la [mesure d'humidité](/techniques/humidimetre) et le test des canalisations en pression. Les évacuations sont contrôlées à la [caméra d'inspection](/techniques/camera-canalisation) ou à la [fluorescéine](/techniques/fluoresceine). Si tout est étanche, c'est une [infiltration](/recherche-infiltration), et le rapport l'établit.",
        ],
      },
      {
        h2: "Facture d'eau anormale",
        body: [
          "Une facture d'eau qui grimpe sans raison, ou un compteur qui tourne alors que tout est fermé, signale souvent une fuite invisible : conduite enterrée entre le compteur et la maison, canalisation sous dalle, chasse d'eau qui coule en continu. Nous vérifions d'abord si la fuite est avant ou après votre compteur, puis nous la localisons. Voir notre page [surconsommation d'eau](/surconsommation-eau).",
        ],
      },
      {
        h2: 'Maisons anciennes de Lasauvage et des vieux quartiers',
        body: [
          "À Lasauvage et dans les quartiers anciens, les maisons de mineurs et d'ouvriers ont des murs épais et des installations reprises au fil des décennies. On y retrouve les points faibles classiques : canalisations anciennes en acier galvanisé ou en plomb, raccords entre matériaux différents, évacuations en fonte fissurées.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Differdange',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "Mon logement a moins de dix ans et il y a une fuite : que faire ?",
        a: "Ne faites pas réparer avant d'avoir documenté l'origine de la fuite. Prévenez votre assureur et le promoteur ou l'entreprise par écrit. Notre rapport décrit ce qui fuit, où et pourquoi : c'est la base pour faire jouer les garanties. Pour l'aspect juridique, adressez-vous à votre assureur protection juridique ou à un avocat.",
      },
      {
        q: 'Ma cave est humide : est-ce forcément une fuite ?',
        a: "Non. L'eau peut venir d'une canalisation, d'une évacuation, du terrain ou de remontées capillaires. Nous testons les canalisations en pression et mesurons l'humidité pour établir laquelle de ces causes est en jeu.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-infiltration', '/recherche-fuite-esch-sur-alzette'],
  },
  {
    path: '/recherche-fuite-dudelange',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Dudelange',
    summary: 'Maisons divisées en appartements, chauffage central ancien, extensions et vérandas.',
    title: 'Recherche de fuite à Dudelange | Sans casse | Expomis',
    description: 'Recherche de fuite non destructive à Dudelange : maison divisée en appartements, chaudière qui perd de la pression, véranda qui fuit. Rapport sous 72h.',
    eyebrow: 'Dudelange',
    h1: "Recherche de fuite d'eau à Dudelange",
    areaServed: [{ '@type': 'City', name: 'Dudelange' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans tous les quartiers de Dudelange : centre, Italie, Brill, Burange, Budersberg, Ribeschpont et les autres.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur, à votre propriétaire ou aux autres occupants de l'immeuble.",
    ],
    sections: [
      {
        h2: 'Maison divisée en appartements : qui fuit, et qui paie l’eau ?',
        body: [
          "À Dudelange comme dans tout le sud du pays, beaucoup d'anciennes maisons unifamiliales ont été divisées en deux ou trois logements. Les canalisations y ont été prolongées plutôt que refaites, et il n'y a souvent qu'un seul compteur d'eau pour tout le bâtiment.",
          "Quand la consommation s'envole ou que l'eau apparaît dans le logement du dessous, chacun soupçonne l'autre. Nous testons en pression les circuits de chaque logement pour établir lequel fuit, puis nous localisons le point de fuite. Le rapport établit les faits, ce qui permet de répartir les frais sur une base objective. Voir aussi notre page [surconsommation d'eau](/surconsommation-eau).",
        ],
      },
      {
        h2: 'Chaudière qui perd de la pression',
        body: [
          "Dans les maisons des années 1960 à 1980, nombreuses à Dudelange, les tuyaux de chauffage passent souvent dans la chape, sous le carrelage ou le parquet. Après des décennies, ils se corrodent et finissent par fuir. Le premier signe est une chaudière qu'il faut recharger en eau régulièrement, sans aucune trace visible.",
          "Nous isolons le circuit de chauffage, confirmons la fuite par un [contrôle de pression](/techniques/mise-en-pression), puis la [caméra thermique](/techniques/camera-thermique) suit le trajet de l'eau chaude dans le sol et fait apparaître le point de fuite. Si l'eau ne laisse pas de signature thermique nette, le [gaz traceur](/techniques/gaz-traceur) prend le relais. Plus de détails sur notre page [fuite de chauffage](/recherche-fuite-chauffage).",
        ],
      },
      {
        h2: 'Extensions, vérandas et toits plats',
        body: [
          "Beaucoup de maisons ont été agrandies : extension à toit plat à l'arrière, véranda, garage accolé. Le point faible est presque toujours la jonction entre l'ancien et le nouveau, c'est-à-dire le relevé d'étanchéité contre la façade, la noue ou le solin. L'eau y entre par temps de pluie et ressort parfois loin de son point d'entrée, au plafond ou le long d'un mur intérieur.",
          "Nous procédons par mises en eau ciblées, zone par zone, avec de la [fluorescéine](/techniques/fluoresceine) si nécessaire, pour relier l'eau qui apparaît à l'endroit précis où elle entre. Voir notre page [recherche d'infiltration](/recherche-infiltration).",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Dudelange',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "Nous partageons un seul compteur d'eau et la consommation a doublé : comment savoir qui fuit ?",
        a: "En testant séparément les circuits de chaque logement. Celui dont le circuit ne tient pas la pression est en cause. Nous localisons alors la fuite, et le rapport établit les faits pour tous les occupants.",
      },
      {
        q: 'Ma chaudière perd de la pression : est-ce forcément une fuite ?',
        a: "Pas toujours. Un vase d'expansion défaillant ou une soupape de sécurité qui évacue de l'eau donnent le même symptôme. Faites-les vérifier par votre chauffagiste. S'ils sont en bon état et que la pression baisse encore, il y a une fuite sur le circuit, et nous la localisons.",
      },
      {
        q: "De l'eau apparaît dans ma véranda : fuite ou condensation ?",
        a: "La condensation apparaît par temps froid, sur toute la surface vitrée, même sans pluie. Une infiltration apparaît pendant ou après la pluie, à un endroit précis, souvent près d'une jonction. Si le doute persiste, une mise en eau ciblée permet de trancher.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-fuite-chauffage', '/recherche-fuite-esch-sur-alzette'],
  },
  {
    path: '/recherche-fuite-petange',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Pétange',
    summary: 'Pétange, Rodange et Lamadelaine : logements loués, humidité persistante, caves inondées.',
    title: 'Recherche de fuite à Pétange, Rodange, Lamadelaine | Expomis',
    description: "Recherche de fuite non destructive à Pétange, Rodange et Lamadelaine : logement loué, humidité après réparation, cave inondée par l'orage. Rapport sous 72h.",
    eyebrow: 'Pétange',
    h1: "Recherche de fuite d'eau à Pétange, Rodange et Lamadelaine",
    areaServed: [{ '@type': 'City', name: 'Pétange' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans toute la commune de Pétange : Pétange, Rodange et Lamadelaine.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur, à votre locataire ou à votre propriétaire.",
    ],
    sections: [
      {
        h2: 'Vous louez un logement et vous ne pouvez pas vous déplacer',
        body: [
          "Beaucoup de logements de la commune sont loués, et leur propriétaire n'habite pas forcément à côté. Quand le locataire signale une fuite, il est difficile de juger à distance de la gravité, de l'urgence et de l'origine.",
          "Nous convenons du rendez-vous directement avec le locataire, nous intervenons et nous vous envoyons le rapport : photos, mesures, origine de la fuite, dommages constatés, emplacement marqué pour le réparateur. Vous décidez de la suite sur pièces, et vous transmettez le même document à votre assureur.",
        ],
      },
      {
        h2: 'L’humidité persiste après la réparation',
        body: [
          "La fuite a été réparée, mais le mur ou le sol reste humide des semaines plus tard. Deux explications sont possibles : les matériaux sèchent lentement, ce qui est normal pour une chape ou un mur épais, ou bien il reste une seconde fuite, ou la réparation n'a pas tenu.",
          "Nous refaisons le test des circuits en pression pour vérifier qu'ils sont étanches, puis nous cartographions l'humidité à l'[humidimètre](/techniques/humidimetre). Des mesures répétées à quelques semaines d'intervalle montrent si la zone humide se réduit ou non. Le rapport permet de décider sereinement : attendre le séchage, assécher, ou reprendre la recherche.",
        ],
      },
      {
        h2: "Cave inondée par temps d'orage",
        body: [
          "De l'eau dans la cave à chaque gros orage, et seulement là : ce n'est généralement pas une fuite de canalisation d'eau. Il peut s'agir d'un refoulement du réseau d'égout par un siphon de sol ou un raccordement sans clapet anti-retour, d'une évacuation enterrée cassée, ou d'une [infiltration](/recherche-infiltration) par un mur enterré.",
          "Nous inspectons les évacuations à la [caméra](/techniques/camera-canalisation), contrôlons leur étanchéité au [test fumigène](/techniques/test-fumigene) ou à la [fluorescéine](/techniques/fluoresceine), et identifions par où l'eau entre. Plus de détails sur notre page [fuite d'évacuation et d'égout](/recherche-fuite-evacuation).",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Pétange',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "Je suis propriétaire et je n'habite pas sur place : pouvez-vous intervenir avec le locataire ?",
        a: "Oui. Nous fixons le rendez-vous avec le locataire et nous vous envoyons le rapport complet, avec photos et mesures. Vous n'avez pas besoin d'être présent.",
      },
      {
        q: 'Combien de temps faut-il pour qu’un mur sèche après une fuite ?',
        a: "Cela dépend du matériau, de l'épaisseur et de la quantité d'eau : de quelques semaines à plusieurs mois pour un mur épais ou une chape. Des mesures d'humidité répétées montrent si le séchage progresse. Si la zone humide ne se réduit pas, il faut chercher une autre cause.",
      },
      {
        q: "Ma cave prend l'eau à chaque orage : est-ce une fuite ?",
        a: "Rarement une fuite d'eau potable. C'est plus souvent un refoulement d'égout, une évacuation enterrée défectueuse ou une infiltration par les murs. Nous identifions laquelle de ces causes est en jeu.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-fuite-evacuation', '/recherche-fuite-differdange'],
  },
  {
    path: '/recherche-fuite-mamer',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Mamer',
    summary: 'Mamer, Capellen et Holzem : maisons individuelles, terrasses sur pièce habitée, robinets extérieurs gelés.',
    title: 'Recherche de fuite à Mamer, Capellen et Holzem | Expomis',
    description: "Recherche de fuite non destructive à Mamer, Capellen et Holzem : bouclage d'eau chaude, terrasse qui fuit, robinet extérieur gelé. Rapport sous 72h.",
    eyebrow: 'Mamer — Capellen — Holzem',
    h1: "Recherche de fuite d'eau à Mamer, Capellen et Holzem",
    areaServed: [
      { '@type': 'City', name: 'Mamer' },
      { '@type': 'City', name: 'Capellen' },
      { '@type': 'City', name: 'Holzem' },
    ],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans toute la commune de Mamer : Mamer, Capellen et Holzem. Nos bureaux de [Steinfort](/recherche-fuite-steinfort) sont dans le même canton.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur ou à votre propriétaire.",
    ],
    sections: [
      {
        h2: "Compteur qui tourne : pensez au bouclage d'eau chaude",
        body: [
          "Les grandes maisons de la commune ont souvent un bouclage d'eau chaude : une boucle qui fait circuler l'eau chaude en permanence pour qu'elle arrive tout de suite au robinet. Une fuite sur cette boucle passe facilement inaperçue. L'eau perdue est remplacée en continu, le compteur tourne, la facture d'eau et d'énergie grimpe, et rien ne se voit, parce que la boucle court dans les murs et les chapes.",
          "Comme l'eau y est chaude, la [caméra thermique](/techniques/camera-thermique) suit son trajet et fait apparaître la zone de fuite. Nous confirmons ensuite par [écoute acoustique](/techniques/detection-acoustique). Voir aussi notre page [surconsommation d'eau](/surconsommation-eau).",
        ],
      },
      {
        h2: 'Terrasse au-dessus d’une pièce ou d’un garage',
        body: [
          "Beaucoup de maisons ont une terrasse qui sert de toit à une pièce, à un garage ou à une cave. Quand une tache apparaît au plafond en dessous, l'eau peut venir de l'étanchéité de la terrasse, d'un relevé contre la façade, d'un seuil de porte-fenêtre, ou d'une évacuation de terrasse bouchée ou fissurée.",
          "Nous testons ces points un par un par mises en eau ciblées, avec de la [fluorescéine](/techniques/fluoresceine) si nécessaire, et nous contrôlons l'évacuation à la [caméra d'inspection](/techniques/camera-canalisation). Le rapport indique précisément où l'eau entre : on refait ce point au lieu de toute la terrasse. Voir notre page [recherche d'infiltration](/recherche-infiltration).",
        ],
      },
      {
        h2: 'Robinet extérieur : la fuite se révèle au printemps',
        body: [
          "Un robinet extérieur mal purgé avant l'hiver peut geler. Le tuyau éclate souvent à l'intérieur du mur, côté maison, là où on ne le voit pas. Rien ne se passe tant que le robinet reste fermé et que le gel bloque l'eau ; la fuite se révèle à la première utilisation au printemps, quand l'eau part dans le mur ou la cave.",
          "Même logique pour les conduites enterrées qui alimentent un abri de jardin, un garage séparé ou un arrosage automatique. Le [contrôle de pression](/techniques/mise-en-pression) confirme la fuite, puis le [gaz traceur](/techniques/gaz-traceur) indique où ouvrir.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Mamer, Capellen et Holzem',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "Mon compteur d'eau tourne alors que tout est fermé : que faire ?",
        a: "Vérifiez d'abord les chasses d'eau, l'adoucisseur et le groupe de sécurité du chauffe-eau, qui peuvent couler en continu. Si le compteur tourne toujours, il y a une fuite sur le réseau. Notez l'index du compteur, prévenez votre assureur et appelez-nous.",
      },
      {
        q: "Comment éviter qu'un robinet extérieur gèle ?",
        a: "Avant l'hiver, fermez la vanne d'arrêt intérieure qui l'alimente, puis ouvrez le robinet extérieur pour vider le tuyau. Laissez-le ouvert jusqu'au printemps. Un robinet antigel posé par un installateur évite ce souci.",
      },
      {
        q: 'Faut-il refaire toute la terrasse pour arrêter une infiltration ?',
        a: "Pas forcément. Si l'infiltration vient d'un point précis, comme un relevé, un seuil ou une évacuation, la réparation de ce point suffit souvent. C'est pour cela qu'il faut d'abord localiser l'entrée d'eau.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/surconsommation-eau', '/recherche-infiltration'],
  },
  {
    path: '/recherche-fuite-strassen',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Strassen',
    summary: 'Bureaux et commerces de la route d’Arlon, appartements et maisons : faux plafonds, climatisation, cuisines.',
    title: 'Recherche de fuite à Strassen | Bureaux et logements | Expomis',
    description: "Recherche de fuite non destructive à Strassen : bureaux et commerces de la route d'Arlon, eau qui goutte d'un faux plafond, parquet gonflé. Rapport sous 72h.",
    eyebrow: 'Strassen',
    h1: "Recherche de fuite d'eau à Strassen",
    areaServed: [{ '@type': 'City', name: 'Strassen' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition à Strassen, dans les logements comme dans les bureaux et les commerces de la route d'Arlon.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur, à votre propriétaire, au syndic ou au gestionnaire de l'immeuble.",
    ],
    sections: [
      {
        h2: "Bureaux et commerces : l'eau qui tombe du faux plafond",
        body: [
          "Le long de la route d'Arlon, Strassen compte de nombreux immeubles de bureaux, commerces et showrooms. Quand l'eau goutte d'un faux plafond, trois origines reviennent :",
        ],
        list: [
          "l'évacuation des condensats de la climatisation, bouchée, déboîtée ou à contre-pente : l'eau ne coule que quand la climatisation tourne ;",
          "une canalisation d'eau ou de chauffage qui passe dans le plénum, ou une évacuation de l'étage du dessus ;",
          "une [infiltration](/recherche-infiltration) par la toiture plate ou la façade, qui ne se manifeste que par temps de pluie.",
        ],
        after: [
          "Le moment où l'eau apparaît oriente déjà le diagnostic. Nous ouvrons ensuite une dalle de faux plafond, inspectons le plénum, à l'[endoscopie](/techniques/endoscopie) si besoin, et testons les réseaux suspects. Nous convenons avec vous du moment de l'intervention pour limiter la gêne pour l'activité. Le rapport est rédigé pour le gestionnaire de l'immeuble et les assureurs.",
        ],
      },
      {
        h2: 'Parquet qui gonfle près de la cuisine',
        body: [
          "Dans les cuisines ouvertes, l'eau arrive souvent par le sol : îlot central avec évier ou plaque, réfrigérateur américain raccordé à l'eau, lave-vaisselle intégré. Une petite fuite sur un flexible, un raccord ou une évacuation de l'îlot s'infiltre sous le parquet, qui gonfle et noircit longtemps avant qu'on voie de l'eau.",
          "Nous vérifions d'abord les appareils et leurs raccordements, puis nous testons les canalisations encastrées dans la chape. La [mesure d'humidité](/techniques/humidimetre) délimite la zone touchée et la [caméra thermique](/techniques/camera-thermique) ou le [gaz traceur](/techniques/gaz-traceur) localisent la fuite. Le rapport décrit l'origine et l'étendue des dommages pour votre assureur.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Strassen',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "De l'eau goutte du faux plafond de nos bureaux : est-ce une fuite ?",
        a: "Pas forcément. Notez quand l'eau apparaît. Si c'est seulement quand la climatisation fonctionne, l'évacuation des condensats est la première suspecte. Si c'est par temps de pluie, c'est probablement une infiltration. Si c'est en permanence, il s'agit plutôt d'une canalisation. Nous confirmons et localisons l'origine.",
      },
      {
        q: "Pouvez-vous intervenir sans arrêter l'activité ?",
        a: "Dans la plupart des cas, oui. Les techniques non destructives demandent peu d'ouvertures, et nous convenons avec vous du moment de l'intervention.",
      },
      {
        q: 'Mon parquet gonfle près de la cuisine : que faire ?',
        a: "Fermez les vannes d'alimentation des appareils (lave-vaisselle, réfrigérateur), photographiez le parquet et prévenez votre assureur. Évitez de démonter le parquet ou les meubles avant notre passage : cela complique la localisation.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-fuite-luxembourg-ville', '/degat-des-eaux'],
  },
  {
    path: '/recherche-fuite-bertrange',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Bertrange',
    summary: 'Bertrange et Helfent : plafond taché sous la salle de bain, humidité après des travaux de rénovation.',
    title: 'Recherche de fuite à Bertrange | Sans casse | Expomis',
    description: 'Recherche de fuite non destructive à Bertrange et Helfent : tache au plafond sous la salle de bain, fuite ou condensation après rénovation. Rapport sous 72h.',
    eyebrow: 'Bertrange',
    h1: "Recherche de fuite d'eau à Bertrange",
    areaServed: [{ '@type': 'City', name: 'Bertrange' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans toute la commune de Bertrange, Helfent compris, dans les maisons comme dans les résidences.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur, à votre propriétaire ou à l'entreprise qui a fait les travaux.",
    ],
    sections: [
      {
        h2: 'Tache au plafond sous la salle de bain',
        body: [
          "C'est la situation la plus courante dans les maisons à étage : une auréole apparaît au plafond du salon ou de la cuisine, juste sous la salle de bain. Trois origines sont possibles, et chacune se teste différemment :",
        ],
        list: [
          "l'alimentation en eau (une canalisation sous pression qui fuit en permanence) : un [contrôle de pression](/techniques/mise-en-pression) circuit par circuit le confirme ;",
          "l'évacuation de la douche, de la baignoire ou du lavabo (la fuite ne coule que quand on vide l'eau) : de l'eau colorée à la [fluorescéine](/techniques/fluoresceine), versée appareil par appareil, montre lequel est en cause ;",
          "l'étanchéité : joints de carrelage, joint silicone de la baignoire, receveur ou paroi de douche. Une mise en eau ciblée de chaque zone permet de trancher.",
        ],
        after: [
          "Refaire les joints au hasard règle rarement le problème si l'eau vient d'un raccord encastré. Nous établissons laquelle des trois causes est en jeu avant toute réparation. Voir aussi notre page [fuite de douche et sous carrelage](/recherche-fuite-douche).",
        ],
      },
      {
        h2: 'Après des travaux de rénovation : fuite, malfaçon ou condensation ?',
        body: [
          "Beaucoup de maisons de la commune sont rénovées : nouvelle salle de bain, nouvelle cuisine, isolation, fenêtres. Deux problèmes apparaissent souvent dans les mois qui suivent.",
          "Une fuite sur l'installation neuve : raccord mal serti, évacuation mal emboîtée, étanchéité de douche absente. Notre rapport décrit précisément ce qui fuit et pourquoi, ce qui vous permet de vous adresser à l'entreprise qui a réalisé les travaux sur une base factuelle.",
          "De l'humidité et des moisissures sans aucune fuite : une maison mieux isolée et des fenêtres étanches retiennent la vapeur d'eau produite par les occupants. Si la ventilation ne suit pas, elle se condense sur les points froids (angles, encadrements de fenêtres, derrière les meubles). Nous testons les canalisations en pression et cartographions l'humidité : si tout est étanche, le rapport l'établit, et la solution passe par la ventilation et non par la plomberie.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Bertrange',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "J'ai refait les joints de la douche, mais la tache au plafond revient : pourquoi ?",
        a: "Parce que l'eau vient probablement d'ailleurs : un raccord d'alimentation ou d'évacuation encastré, ou l'étanchéité sous le carrelage. Il faut tester l'alimentation, l'évacuation et l'étanchéité séparément pour trouver la vraie origine.",
      },
      {
        q: "Des moisissures sont apparues depuis que nous avons changé les fenêtres : est-ce une fuite ?",
        a: "Le plus souvent non. C'est de la condensation : les nouvelles fenêtres sont étanches et la vapeur d'eau ne s'évacue plus. Aérez quotidiennement et faites vérifier la ventilation. Si les taches sont localisées près d'une canalisation ou ne correspondent pas à des points froids, une recherche de fuite permet d'écarter le doute.",
      },
      {
        q: "Une fuite est apparue après les travaux du plombier : que faire ?",
        a: "Prévenez l'entreprise par écrit et déclarez le sinistre à votre assureur. Avant que la fuite ne soit réparée, faites établir son origine exacte : notre rapport, avec photos et mesures, sert de base à la discussion avec l'entreprise et son assurance.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-fuite-douche', '/recherche-fuite-mamer'],
  },
  {
    path: '/recherche-fuite-hesperange',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Hesperange',
    summary: 'Hesperange, Alzingen, Fentange, Howald et Itzig : séchage après une crue, colonnes en fonte, flexibles qui éclatent.',
    title: 'Recherche de fuite à Hesperange, Howald, Itzig | Expomis',
    description: 'Recherche de fuite non destructive à Hesperange, Howald, Itzig, Alzingen, Fentange : séchage après crue, colonne en fonte, flexible éclaté. Rapport sous 72h.',
    eyebrow: 'Hesperange',
    h1: "Recherche de fuite d'eau à Hesperange",
    areaServed: [
      { '@type': 'City', name: 'Hesperange' },
      { '@type': 'City', name: 'Howald' },
      { '@type': 'City', name: 'Itzig' },
    ],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans toute la commune de Hesperange : Hesperange, Alzingen, Fentange, Howald et Itzig.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite et constate les dommages, à transmettre à votre assureur, à votre propriétaire ou au syndic.",
    ],
    sections: [
      {
        h2: "Au bord de l'Alzette : après une crue, quand peut-on refaire ?",
        body: [
          "Hesperange et Alzingen sont traversées par l'Alzette, et les maisons proches de la rivière peuvent voir leur cave ou leur rez-de-chaussée envahis lors d'une crue. Une fois l'eau partie, la question devient : quand les murs et les sols seront-ils assez secs pour refaire les enduits, poser un revêtement ou remonter des cloisons ? Refaire trop tôt, c'est enfermer l'humidité, avec des moisissures et des revêtements qui se décollent quelques mois plus tard.",
          "Nous cartographions l'humidité des murs et des sols à l'[humidimètre](/techniques/humidimetre), puis nous répétons les mesures à intervalles réguliers. Vous savez ainsi où le séchage progresse, où il stagne, et à partir de quand les travaux peuvent reprendre. Le rapport documente aussi l'état après sinistre pour votre assureur. Voir notre page [dégât des eaux](/degat-des-eaux).",
          "Si l'humidité persiste dans une zone alors que tout le reste a séché, nous vérifions qu'aucune canalisation n'a été endommagée : une conduite ou une évacuation fissurée peut entretenir l'humidité longtemps après la crue.",
        ],
      },
      {
        h2: "Maisons des années 1950 à 1970 : les colonnes d'évacuation en fonte",
        body: [
          "Dans les maisons de cette époque, nombreuses dans la commune, les descentes d'évacuation intérieures sont souvent en fonte, encastrées dans un mur ou une gaine. Avec le temps, la fonte se corrode de l'intérieur et finit par se fendre, parfois sur toute sa longueur. Les signes : une tache qui suit la verticale d'un mur, une odeur d'égout, de l'humidité qui apparaît surtout quand l'étage du dessus utilise l'eau.",
          "La [caméra d'inspection](/techniques/camera-canalisation), descendue depuis le haut de la colonne ou depuis un regard, montre l'état de la fonte et situe la fissure. Le [test fumigène](/techniques/test-fumigene) confirme les fuites d'odeur. Vous savez alors s'il faut remplacer un tronçon ou toute la colonne. Voir notre page [fuite d'évacuation et d'égout](/recherche-fuite-evacuation).",
        ],
      },
      {
        h2: 'Flexible qui éclate : le dégât des eaux soudain',
        body: [
          "Un flexible d'alimentation qui cède sous un évier, un lavabo, un WC ou derrière un lave-linge provoque en quelques minutes un dégât des eaux important, souvent pendant une absence. La cause est généralement la fatigue du flexible, parfois aggravée par une pression d'eau élevée ou par un réducteur de pression qui ne joue plus son rôle.",
          "Dans ce cas, la fuite est évidente ; le travail consiste à constater l'étendue des dommages, y compris là où l'eau s'est infiltrée sans se voir (sous les sols, dans les murs, chez le voisin du dessous), et à vérifier qu'aucune autre partie de l'installation n'a souffert. Faites aussi contrôler la pression par votre installateur : un réducteur défaillant fatigue tous les raccords de la maison.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Hesperange',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: 'Combien de temps faut-il pour que les murs sèchent après une crue ?',
        a: "Cela dépend de la hauteur d'eau, de la durée de l'immersion, de l'épaisseur et du matériau des murs, et de la ventilation : de plusieurs semaines à plusieurs mois. Seules des mesures d'humidité répétées permettent de savoir quand reprendre les travaux.",
      },
      {
        q: "Comment savoir si ma colonne d'évacuation en fonte est en fin de vie ?",
        a: "Une inspection caméra montre l'état intérieur : corrosion, dépôts, fissures. Des taches verticales sur un mur ou des odeurs d'égout récurrentes sont des signaux d'alerte.",
      },
      {
        q: 'Un flexible a éclaté pendant mon absence. Que faire ?',
        a: "Fermez l'arrivée d'eau, coupez l'électricité des pièces touchées, photographiez tout et déclarez le sinistre à votre assureur. Prévenez le voisin du dessous s'il y en a un. Nous pouvons constater l'étendue des dommages, y compris ceux qui ne se voient pas encore.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/degat-des-eaux', '/recherche-fuite-evacuation'],
  },
  {
    path: '/recherche-fuite-sanem',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Sanem',
    summary: 'Sanem, Belvaux, Soleuvre et Ehlerange : combles aménagés, fenêtres de toit, locaux d’activité.',
    title: 'Recherche de fuite à Sanem, Belvaux, Soleuvre | Expomis',
    description: "Recherche de fuite et d'infiltration à Sanem, Belvaux, Soleuvre et Ehlerange : combles aménagés, fenêtres de toit, locaux d'activité. Rapport sous 72h.",
    eyebrow: 'Sanem',
    h1: "Recherche de fuite d'eau à Sanem, Belvaux et Soleuvre",
    areaServed: [
      { '@type': 'City', name: 'Sanem' },
      { '@type': 'City', name: 'Belvaux' },
      { '@type': 'City', name: 'Soleuvre' },
    ],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans toute la commune de Sanem : Sanem, Belvaux, Soleuvre et Ehlerange, dans les maisons, les résidences et les locaux d'activité.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur, à votre propriétaire ou à votre couvreur.",
    ],
    sections: [
      {
        h2: 'Combles aménagés : fenêtre de toit, cheminée ou noue ?',
        body: [
          "Beaucoup de maisons de la commune ont des combles aménagés en chambres ou en bureau. Sous une toiture en pente, l'eau qui entre suit les chevrons, l'isolant ou le pare-vapeur avant de réapparaître, parfois à plusieurs mètres de son point d'entrée. La tache au plafond ne désigne donc pas l'endroit à réparer.",
          "Les points faibles reviennent toujours : le raccord d'une fenêtre de toit, la sortie de cheminée ou de ventilation, la noue où deux pans se rejoignent, le faîtage. Souvent, l'eau n'entre que par pluie battante et par un vent d'une certaine direction, ce qui explique les fuites « qui vont et viennent ».",
          "Nous repérons d'abord l'étendue de l'humidité à la [caméra thermique](/techniques/camera-thermique), qui fait apparaître l'isolant mouillé sans rien démonter. Nous arrosons ensuite les points suspects un par un, avec de la [fluorescéine](/techniques/fluoresceine) si nécessaire, pour désigner au couvreur le point exact à reprendre. Voir notre page [recherche d'infiltration](/recherche-infiltration).",
        ],
      },
      {
        h2: "Locaux d'activité à Ehlerange",
        body: [
          "Dans les ateliers, entrepôts et bureaux de la zone d'activités d'Ehlerange, les réseaux sont plus étendus que dans une maison : longues conduites enterrées entre bâtiments, sanitaires éloignés du compteur, grandes toitures plates avec de nombreuses évacuations.",
          "Une surconsommation sur le compteur général se localise par élimination : nous isolons les tronçons un par un pour savoir lequel perd de l'eau, puis l'[écoute acoustique](/techniques/detection-acoustique) ou le [gaz traceur](/techniques/gaz-traceur) situent la fuite sous la dalle ou dans la cour. Sur les toitures plates, nous testons relevés et évacuations zone par zone. Nous convenons avec vous du moment de l'intervention pour limiter la gêne pour l'activité.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Sanem',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "La tache au plafond de mes combles n'apparaît que par certains jours de pluie. Pourquoi ?",
        a: "Parce que l'eau n'entre souvent que par pluie battante et par vent d'une certaine direction, qui la pousse sous une tuile, un raccord de fenêtre de toit ou un solin. Les mises en eau ciblées reproduisent ces conditions point par point, sans attendre la prochaine averse.",
      },
      {
        q: 'Faut-il monter sur le toit pour trouver une infiltration ?',
        a: "Certaines mises en eau se font depuis l'extérieur. Si la toiture n'est pas accessible en sécurité, nous convenons avec vous ou votre couvreur des moyens d'accès. Une grande partie du diagnostic se fait depuis l'intérieur, à la caméra thermique et à l'humidimètre.",
      },
      {
        q: "Notre compteur général d'entreprise tourne la nuit : que faire ?",
        a: "Notez l'index le soir et le matin, quand rien ne fonctionne : l'écart mesure la fuite. Vérifiez les chasses d'eau et les équipements qui consomment de l'eau, puis appelez-nous pour isoler les tronçons et localiser la fuite.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-infiltration', '/recherche-fuite-esch-sur-alzette'],
  },
  {
    path: '/recherche-fuite-mersch',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Mersch',
    summary: 'Mersch, Beringen, Rollingen, Reckange, Pettingen, Schoenfels : sous-sols aménagés, fermes et maisons de village rénovées.',
    title: 'Recherche de fuite à Mersch, Beringen, Rollingen | Expomis',
    description: "Recherche de fuite et d'humidité non destructive à Mersch : sous-sol aménagé humide, condensation d'été, ferme ou maison de village rénovée. Rapport sous 72h.",
    eyebrow: 'Mersch',
    h1: "Recherche de fuite d'eau à Mersch",
    areaServed: [{ '@type': 'City', name: 'Mersch' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans toute la commune de Mersch : Mersch, Beringen, Rollingen, Reckange, Pettingen, Schoenfels et les autres localités.",
      "Vous recevez sous 72h un rapport qui situe l'origine de l'eau, à transmettre à votre assureur, à votre propriétaire ou à l'entreprise qui fera les travaux.",
    ],
    sections: [
      {
        h2: "Sous-sol aménagé : fuite, infiltration ou condensation d'été ?",
        body: [
          "Bureau, salle de jeux, chambre d'amis, buanderie : beaucoup de sous-sols sont aménagés. Quand une odeur de moisi apparaît, qu'une tache se forme derrière un meuble ou qu'un doublage se tache en pied de mur, trois causes sont possibles, et chacune se traite différemment :",
        ],
        list: [
          "une fuite sur une canalisation : arrivée d'eau, chauffage, évacuation qui passe sous la dalle ou dans le mur ;",
          "une [infiltration](/recherche-infiltration) par un mur enterré dont l'étanchéité extérieure a vieilli, ou par un saut-de-loup ;",
          "de la condensation, surtout en été : l'air chaud et humide de l'extérieur entre dans un sous-sol resté frais, et l'humidité se dépose sur les murs froids.",
        ],
        after: [
          "La condensation d'été est souvent prise pour une fuite. Elle apparaît par temps chaud et lourd, sur les murs les plus froids, et diminue quand on aère la nuit plutôt que le jour. Nous testons les canalisations en pression, cartographions l'humidité à l'[humidimètre](/techniques/humidimetre) et regardons derrière les doublages à l'[endoscopie](/techniques/endoscopie), par un petit perçage. Le rapport désigne la cause : plomberie, étanchéité extérieure ou ventilation.",
        ],
      },
      {
        h2: 'Corps de ferme et maisons de village rénovés',
        body: [
          "Dans les localités de la commune, beaucoup d'anciennes fermes et maisons de village ont été transformées en logements. Les murs en moellons y font souvent plus de cinquante centimètres d'épaisseur, et les installations neuves (canalisations multicouches, chauffage au sol, salles de bain) ont été intégrées à un bâti qui n'était pas prévu pour elles.",
          "Deux situations reviennent. Une tache au pied d'un mur épais peut venir d'une canalisation encastrée, mais aussi de remontées capillaires ou d'un enduit étanche qui empêche le mur de sécher : la [mesure d'humidité](/techniques/humidimetre) à différentes hauteurs et un test des canalisations en pression permettent de trancher. Et le [chauffage au sol](/recherche-fuite-chauffage) coulé sur un sol ancien qui perd de la pression se localise boucle par boucle, puis à la [caméra thermique](/techniques/camera-thermique).",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Mersch',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "Ma cave n'est humide qu'en été : est-ce une fuite ?",
        a: "Probablement pas. C'est le signe typique de la condensation : l'air chaud et humide se refroidit au contact des murs frais du sous-sol. Aérez la nuit et le matin tôt plutôt que l'après-midi, et surveillez l'évolution. Si l'humidité persiste en hiver ou se concentre à un endroit précis, une recherche de fuite permet d'écarter le doute.",
      },
      {
        q: 'Des moisissures apparaissent derrière le doublage de mon sous-sol. Faut-il tout démonter ?',
        a: "Pas pour le diagnostic. Un petit perçage suffit pour regarder derrière le doublage à l'endoscope et mesurer l'humidité. Une fois la cause connue, on sait ce qu'il faut ouvrir, et où.",
      },
      {
        q: "Le mur de ma ferme rénovée est humide en pied : fuite ou remontées capillaires ?",
        a: "Les deux sont possibles. Une fuite donne une humidité concentrée autour d'un point, qui varie avec l'usage de l'eau ; les remontées capillaires donnent une humidité plus régulière, décroissante avec la hauteur. Les mesures d'humidité et le test des canalisations en pression permettent de trancher.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-infiltration', '/recherche-fuite-chauffage'],
  },
  {
    path: '/recherche-fuite-ettelbruck',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Ettelbruck',
    summary: 'Ettelbruck, Warken et Grentzingen : chauffage collectif qui perd de la pression, maison inoccupée, gel.',
    title: 'Recherche de fuite à Ettelbruck, Warken | Expomis',
    description: 'Recherche de fuite non destructive à Ettelbruck, Warken et Grentzingen : chauffage collectif qui perd de la pression, maison inoccupée, gel. Rapport sous 72h.',
    eyebrow: 'Ettelbruck',
    h1: "Recherche de fuite d'eau à Ettelbruck",
    areaServed: [{ '@type': 'City', name: 'Ettelbruck' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition dans toute la commune d'Ettelbruck, Warken et Grentzingen compris, ainsi que dans le reste de la Nordstad.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur, à votre propriétaire ou au syndic.",
    ],
    sections: [
      {
        h2: 'Immeuble avec chauffage collectif qui perd de la pression',
        body: [
          "Dans les immeubles à appartements équipés d'une chaufferie commune, une fuite sur le réseau de chauffage se manifeste d'abord en chaufferie : la pression baisse, et il faut remettre de l'eau régulièrement. Le réseau est long (colonnes montantes, distribution en cave, radiateurs de chaque logement) et la fuite peut être n'importe où, y compris dans un appartement dont l'occupant ne remarque rien.",
          "Nous procédons par isolement : en fermant les vannes par colonne puis par logement, et en suivant la pression, nous réduisons la zone en cause. La [caméra thermique](/techniques/camera-thermique) et l'[écoute acoustique](/techniques/detection-acoustique) localisent ensuite la fuite sur le tronçon identifié. Le rapport indique si elle se trouve sur une partie commune ou dans un logement, ce dont le syndic a besoin pour savoir qui prend en charge la réparation. Voir aussi notre guide [Fuite d'eau : qui paie ?](/fuite-eau-qui-paie).",
        ],
      },
      {
        h2: 'Maison inoccupée : gel et fuite découverte tard',
        body: [
          "Une maison vide pendant une succession, une mise en vente, des travaux ou entre deux locations est exposée à deux risques. En hiver, si le chauffage est coupé ou trop bas, les canalisations d'une cave, d'un garage ou d'un mur extérieur peuvent geler et se fendre ; la fuite se déclare au dégel, parfois des semaines plus tard. Et une fuite qui démarre dans une maison inoccupée peut couler longtemps avant d'être remarquée.",
          "Quand vous découvrez les dégâts, il faut à la fois trouver toutes les fuites (le gel en provoque souvent plusieurs), et constater l'étendue des dommages, y compris l'humidité dans les murs et sous les sols. Nous testons chaque circuit en pression, localisons chaque point de fuite et cartographions l'humidité. Le rapport documente l'ensemble pour votre assureur.",
          "Certains contrats d'assurance prévoient des obligations en cas d'inoccupation prolongée, comme couper l'eau ou vidanger l'installation, ou limitent la garantie après une certaine durée d'inoccupation. Vérifiez vos conditions avant de laisser une maison vide pendant l'hiver.",
        ],
      },
      {
        h2: "Ce que nous recherchons à Ettelbruck",
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: 'La chaufferie de notre immeuble perd de la pression : qui doit faire rechercher la fuite ?',
        a: "En général le syndic, puisque la chaufferie et le réseau de distribution sont des parties communes. Si la fuite est ensuite localisée dans un logement, la répartition des frais dépend du règlement de copropriété. Notre rapport indique précisément où se trouve la fuite.",
      },
      {
        q: 'Comment protéger une maison inoccupée en hiver ?',
        a: "Maintenez un chauffage minimal hors gel, fermez la vanne générale d'eau, et faites vidanger les canalisations exposées au froid si la maison reste vide longtemps. Vérifiez aussi ce que votre contrat d'assurance exige en cas d'inoccupation.",
      },
      {
        q: 'Après un gel, peut-il y avoir plusieurs fuites ?',
        a: "Oui, c'est fréquent : l'eau gèle à plusieurs endroits exposés en même temps. Il faut tester chaque circuit séparément pour être sûr de les avoir toutes trouvées avant de remettre l'installation en service.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-fuite-diekirch', '/fuite-eau-qui-paie'],
  },
  {
    path: '/recherche-fuite-diekirch',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Diekirch',
    summary: "Diekirch et la Nordstad : fuites qui vont et viennent, recherche déjà faite sans résultat.",
    title: 'Recherche de fuite à Diekirch | Fuite introuvable | Expomis',
    description: 'Recherche de fuite non destructive à Diekirch : fuite intermittente, tache qui revient, recherche déjà faite sans résultat. Rapport sous 72h.',
    eyebrow: 'Diekirch',
    h1: "Recherche de fuite d'eau à Diekirch",
    areaServed: [{ '@type': 'City', name: 'Diekirch' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition à Diekirch et dans le reste de la Nordstad, dans les maisons comme dans les immeubles.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur, à votre propriétaire ou au syndic.",
    ],
    sections: [
      {
        h2: 'La fuite qui va et vient',
        body: [
          "Une tache apparaît, sèche, puis revient. Ces fuites intermittentes sont les plus difficiles à trouver, parce qu'elles ne se manifestent pas forcément le jour de la visite. Le moment où l'eau apparaît est le meilleur indice :",
        ],
        list: [
          "seulement quand le chauffage fonctionne : un raccord du circuit de chauffage qui s'ouvre avec la dilatation, à chaud ;",
          "seulement après une douche, une lessive ou une vaisselle : une évacuation, ou l'étanchéité d'une douche, plutôt qu'une canalisation sous pression ;",
          "seulement par temps de pluie : une [infiltration](/recherche-infiltration) par la toiture, la façade ou une terrasse ;",
          "seulement par temps chaud et humide : de la condensation, par exemple sur une conduite d'eau froide mal isolée ;",
          "surtout la nuit : la pression du réseau est souvent plus élevée quand la consommation baisse, ce qui peut suffire à faire suinter un raccord fatigué.",
        ],
        after: [
          "Avant notre passage, notez quand la tache apparaît et photographiez-la avec la date. Sur place, nous reproduisons les conditions suspectes : mise en chauffe du circuit, écoulement d'un appareil précis à la [fluorescéine](/techniques/fluoresceine), mise en eau d'une zone de façade, [contrôle de pression](/techniques/mise-en-pression) prolongé. La fuite se montre alors pendant l'intervention.",
        ],
      },
      {
        h2: "Une première recherche n'a rien donné",
        body: [
          "Un mur a déjà été ouvert à un endroit, puis à un autre, sans trouver la fuite. Ou la réparation a été faite, mais la tache revient. Cela arrive quand on cherche directement le point de fuite sans avoir d'abord établi sur quel circuit il se trouve, ou quand une seule technique a été employée là où il en fallait une autre.",
          "Nous reprenons dans l'ordre : quel réseau est en cause (eau froide, eau chaude, chauffage, évacuation, ou eau venue de l'extérieur), puis quel tronçon, puis quel point. Nous croisons au moins deux méthodes avant de marquer l'emplacement. Le rapport décrit les tests réalisés et leurs résultats, y compris ceux qui écartent une hypothèse : c'est utile si plusieurs intervenants ou assureurs sont concernés.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Diekirch',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "La fuite n'apparaît pas tous les jours. Pouvez-vous quand même la trouver ?",
        a: "Oui, si l'on sait dans quelles conditions elle apparaît. Notez les moments où la tache se forme (chauffage, douche, pluie, nuit) : nous reproduisons ces conditions pendant l'intervention pour que la fuite se manifeste.",
      },
      {
        q: 'Le plombier a déjà ouvert deux fois sans trouver la fuite. Que faire ?',
        a: "Ne faites pas ouvrir une troisième fois au hasard. Une recherche méthodique établit d'abord le circuit en cause, puis le tronçon, puis le point de fuite, en croisant plusieurs techniques. On ouvre ensuite une seule fois, au bon endroit.",
      },
      {
        q: 'Une conduite froide qui « transpire » peut-elle passer pour une fuite ?',
        a: "Oui. Par temps chaud et humide, l'humidité de l'air se condense sur les conduites d'eau froide mal isolées et goutte, ce qui ressemble à une fuite. Un test en pression montre que la conduite est étanche ; une isolation de la conduite règle le problème.",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-fuite-ettelbruck', '/techniques'],
  },
  {
    path: '/recherche-fuite-grevenmacher',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Grevenmacher',
    summary: 'Grevenmacher et la Moselle : caves voûtées, maisons de vigneron, maisons au pied des coteaux.',
    title: 'Recherche de fuite à Grevenmacher et en Moselle | Expomis',
    description: "Recherche de fuite et d'humidité à Grevenmacher et en Moselle : cave voûtée, maison de vigneron, eau de ruissellement, drain bouché. Rapport sous 72h.",
    eyebrow: 'Grevenmacher — Moselle',
    h1: "Recherche de fuite d'eau à Grevenmacher",
    areaServed: [{ '@type': 'City', name: 'Grevenmacher' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition à Grevenmacher et dans les villages de la Moselle luxembourgeoise.",
      "Vous recevez sous 72h un rapport qui situe l'origine de l'eau, à transmettre à votre assureur ou à l'entreprise qui fera les travaux. Une partie de nos informations est aussi disponible en allemand : [Leckortung in Luxemburg](/de/leckortung-luxemburg).",
    ],
    sections: [
      {
        h2: 'Caves voûtées et maisons de vigneron',
        body: [
          "Les maisons anciennes de la Moselle ont souvent une cave voûtée, construite pour garder le vin au frais et à une humidité élevée. Une cave voûtée humide n'a donc rien d'anormal en soi. La question se pose quand l'humidité augmente, se concentre à un endroit, ou quand la cave a été transformée en pièce de vie, en cellier ou en salle de dégustation.",
          "Une canalisation d'eau ou une évacuation qui traverse la voûte ou longe un mur peut fuir sans que rien ne se voie, l'humidité se confondant avec celle de la cave. Nous testons les canalisations en pression, cartographions l'humidité à l'[humidimètre](/techniques/humidimetre) et comparons les zones entre elles : une fuite donne une humidité nettement plus forte autour d'un point, qui ne suit pas le reste de la cave. Le rapport établit si l'humidité est normale pour le bâti ou s'il y a une fuite à réparer.",
        ],
      },
      {
        h2: 'Au pied des coteaux : ruissellement, drain ou fuite ?',
        body: [
          "Beaucoup de maisons sont construites au pied des coteaux. Par forte pluie, l'eau qui ruisselle sur la pente s'accumule contre les murs côté amont. Un drain posé autour des fondations doit l'évacuer ; s'il est bouché par la terre ou les racines, écrasé ou mal raccordé, l'eau stagne contre le mur et finit par entrer en cave.",
          "Nous inspectons le drain et son raccordement à la [caméra](/techniques/camera-canalisation), depuis un regard, pour voir s'il est bouché, cassé ou en contre-pente. Si le drain fonctionne, nous testons les canalisations et les évacuations enterrées du même côté, à la [fluorescéine](/techniques/fluoresceine) si nécessaire. Le rapport indique ce qu'il faut reprendre : curage ou remplacement du drain, étanchéité du mur, ou réparation d'une canalisation. Voir notre page [recherche d'infiltration](/recherche-infiltration).",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Grevenmacher',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: 'Ma cave voûtée est humide : faut-il s’inquiéter ?',
        a: "Pas forcément : une cave voûtée est naturellement fraîche et humide. Il faut s'inquiéter si l'humidité augmente, se concentre à un endroit, laisse apparaître de l'eau, ou si la cave sert de pièce de vie. Des mesures d'humidité et un test des canalisations permettent de savoir s'il y a une fuite.",
      },
      {
        q: "L'eau entre dans ma cave seulement par forte pluie, côté coteau. Que vérifier ?",
        a: "Le drain autour des fondations en premier : s'il est bouché ou cassé, l'eau de ruissellement stagne contre le mur. Une inspection caméra depuis un regard le montre. Vérifiez aussi que les descentes de gouttière ne se déversent pas au pied du mur.",
      },
      {
        q: 'Avez-vous des informations en allemand ?',
        a: "Oui, une partie du site est disponible en allemand, à partir de la page [Leckortung in Luxemburg](/de/leckortung-luxemburg).",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-fuite-remich', '/recherche-infiltration'],
  },
  {
    path: '/recherche-fuite-remich',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Remich',
    summary: 'Remich et la Moselle : hôtels et restaurants, balcons et loggias en résidence.',
    title: 'Recherche de fuite à Remich | Hôtels, résidences | Expomis',
    description: 'Recherche de fuite non destructive à Remich : hôtels et restaurants, salles de bains superposées, balcon ou loggia qui fuit chez le voisin. Rapport sous 72h.',
    eyebrow: 'Remich — Moselle',
    h1: "Recherche de fuite d'eau à Remich",
    areaServed: [{ '@type': 'City', name: 'Remich' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition à Remich et dans les communes voisines de la Moselle, dans les logements comme dans les hôtels, restaurants et commerces.",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur, au syndic ou à l'entreprise qui fera la réparation. Une partie de nos informations est aussi disponible en allemand : [Leckortung in Luxemburg](/de/leckortung-luxemburg).",
    ],
    sections: [
      {
        h2: 'Hôtels et restaurants : localiser vite, sans fermer',
        body: [
          "Dans un hôtel, les salles de bains sont superposées d'étage en étage : une tache au plafond d'une chambre peut venir de la chambre du dessus, de celle d'à côté ou d'une colonne commune. Chaque chambre immobilisée est une chambre qu'on ne loue pas. Nous testons les salles de bains une par une (alimentation, évacuation et étanchéité de la douche séparément) pour désigner celle qui fuit, puis l'élément en cause. Voir notre page [fuite de douche](/recherche-fuite-douche).",
          "En cuisine professionnelle, les évacuations de sol, les siphons et les raccordements des lave-vaisselle et des plonges travaillent beaucoup et s'encrassent. Une fuite sous le carrelage de la cuisine finit souvent au plafond de la cave ou de la salle du dessous. La [fluorescéine](/techniques/fluoresceine), appareil par appareil, et la [caméra d'inspection](/techniques/camera-canalisation) dans les évacuations localisent le défaut.",
          "Nous convenons avec vous du moment de l'intervention pour limiter la gêne pour la clientèle, et le rapport sert à votre assureur et, le cas échéant, au propriétaire des murs.",
        ],
      },
      {
        h2: 'Balcon ou loggia qui fuit chez le voisin du dessous',
        body: [
          "Dans les résidences avec vue sur la Moselle, les balcons et les loggias sont nombreux. Quand l'eau de pluie ou de nettoyage passe à travers, elle apparaît au plafond du balcon ou de la pièce du dessous, ou dans le parking. Les points faibles : l'évacuation du balcon bouchée ou mal raccordée, le relevé d'étanchéité contre la façade, le seuil de la porte-fenêtre, les fixations du garde-corps qui percent l'étanchéité.",
          "Nous testons ces points un par un par mises en eau ciblées. Le rapport indique précisément où l'eau entre, ce qui compte aussi pour savoir qui paie : en copropriété, le balcon est souvent une partie commune dont un seul copropriétaire a l'usage, et le règlement de copropriété précise qui prend en charge l'étanchéité. Voir notre guide [Fuite d'eau : qui paie ?](/fuite-eau-qui-paie).",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Remich',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: 'Une fuite dans notre hôtel : faut-il bloquer plusieurs chambres ?',
        a: "En général non. Nous commençons par la chambre la plus probable, au-dessus de la tache, en testant séparément alimentation, évacuation et étanchéité. Seules les chambres réellement concernées sont immobilisées, le temps des tests.",
      },
      {
        q: 'Qui paie la réparation de l’étanchéité de mon balcon ?',
        a: "Cela dépend du règlement de copropriété. Le balcon est souvent une partie commune à usage privatif : l'étanchéité relève alors de la copropriété, l'entretien courant (nettoyage de l'évacuation) de l'occupant. Le rapport, en désignant le point d'entrée de l'eau, aide le syndic à trancher.",
      },
      {
        q: 'Avez-vous des informations en allemand ?',
        a: "Oui, une partie du site est disponible en allemand, à partir de la page [Leckortung in Luxemburg](/de/leckortung-luxemburg).",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-fuite-grevenmacher', '/fuite-eau-qui-paie'],
  },
  {
    path: '/recherche-fuite-echternach',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Echternach',
    summary: 'Echternach et le Mullerthal : maisons anciennes de la vieille ville, murs et façades en grès.',
    title: 'Recherche de fuite à Echternach et au Mullerthal | Expomis',
    description: "Recherche de fuite et d'infiltration à Echternach et au Mullerthal : maisons anciennes, bâtiments protégés, murs en grès humides. Rapport sous 72h.",
    eyebrow: 'Echternach — Mullerthal',
    h1: "Recherche de fuite d'eau à Echternach",
    areaServed: [{ '@type': 'City', name: 'Echternach' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition à Echternach et dans la région du Mullerthal, dans les maisons anciennes de la vieille ville comme dans les constructions récentes.",
      "Vous recevez sous 72h un rapport qui situe l'origine de l'eau, à transmettre à votre assureur, à votre architecte ou à l'entreprise qui fera les travaux. Une partie de nos informations est aussi disponible en allemand : [Leckortung in Luxemburg](/de/leckortung-luxemburg).",
    ],
    sections: [
      {
        h2: 'Vieille ville : ouvrir le moins possible, et au bon endroit',
        body: [
          "Dans la vieille ville, beaucoup de maisons ont plusieurs siècles. Murs épais, planchers anciens, enduits à la chaux : chaque ouverture coûte cher à refaire dans les règles de l'art. Si le bâtiment est protégé au titre du patrimoine, certains travaux peuvent en plus nécessiter une autorisation préalable.",
          "C'est là que la recherche non destructive prend tout son sens. Nous établissons d'abord le circuit en cause par [contrôle de pression](/techniques/mise-en-pression), puis nous localisons le point de fuite en croisant deux méthodes avant de le marquer : [caméra thermique](/techniques/camera-thermique), [écoute acoustique](/techniques/detection-acoustique), [gaz traceur](/techniques/gaz-traceur). L'[endoscopie](/techniques/endoscopie) permet de vérifier dans un plancher ou un doublage par un perçage de quelques millimètres. Le rapport précise l'emplacement et la surface à ouvrir, ce qui aide aussi à préparer une éventuelle demande d'autorisation.",
        ],
      },
      {
        h2: 'Murs et façades en grès : la pluie qui traverse la pierre',
        body: [
          "Le grès de la région est une pierre poreuse. Sur une façade exposée aux pluies battantes, l'eau peut traverser un mur en grès, surtout si les joints sont dégradés, si la pierre a été recouverte d'un enduit ou d'une peinture qui l'empêche de sécher, ou si un appui de fenêtre renvoie l'eau vers la maçonnerie. L'humidité apparaît alors à l'intérieur, sur le mur exposé, et fait penser à une fuite.",
          "Nous vérifions d'abord que les canalisations voisines sont étanches. Puis nous cartographions l'humidité à l'[humidimètre](/techniques/humidimetre) sur toute la surface du mur, et nous faisons des mises en eau ciblées de la façade, à la [fluorescéine](/techniques/fluoresceine) si nécessaire, pour identifier les zones par lesquelles l'eau entre. Le rapport oriente vers la bonne réparation : rejointoiement, reprise d'un appui ou d'un solin, ou remplacement d'un revêtement qui bloque l'humidité. Voir notre page [recherche d'infiltration](/recherche-infiltration).",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Echternach',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: 'Ma maison est protégée au titre du patrimoine : pouvez-vous intervenir ?',
        a: "Oui. La localisation elle-même est non destructive et ne demande au plus que de petits perçages. Pour la réparation, renseignez-vous sur les autorisations nécessaires avant d'ouvrir ; notre rapport précise où et sur quelle surface il faudra intervenir.",
      },
      {
        q: 'Mon mur en grès est humide seulement après de fortes pluies : est-ce une fuite ?',
        a: "Probablement pas une fuite de canalisation. L'eau de pluie traverse sans doute la maçonnerie, par la pierre ou par les joints. Des mises en eau ciblées de la façade permettent de savoir par où, et donc quoi reprendre.",
      },
      {
        q: 'Avez-vous des informations en allemand ?',
        a: "Oui, une partie du site est disponible en allemand, à partir de la page [Leckortung in Luxemburg](/de/leckortung-luxemburg).",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-infiltration', '/recherche-fuite-diekirch'],
  },
  {
    path: '/recherche-fuite-wiltz',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Wiltz',
    summary: "Wiltz et l'Oesling : infiltrations sous la neige, chauffage au bois, aux pellets ou au mazout.",
    title: "Recherche de fuite à Wiltz et dans l'Oesling | Expomis",
    description: "Recherche de fuite non destructive à Wiltz et dans l'Oesling : infiltration de toiture après la neige, chauffage au bois ou aux pellets. Rapport sous 72h.",
    eyebrow: 'Wiltz — Oesling',
    h1: "Recherche de fuite d'eau à Wiltz",
    areaServed: [{ '@type': 'City', name: 'Wiltz' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations sans démolition à Wiltz, en Haute-Wiltz comme en Basse-Wiltz, à Weidingen, à Eschweiler, et dans tout le nord du pays.",
      "Vous recevez sous 72h un rapport qui situe l'origine de l'eau, à transmettre à votre assureur ou à l'entreprise qui fera la réparation. Une partie de nos informations est aussi disponible en allemand : [Leckortung in Luxemburg](/de/leckortung-luxemburg).",
    ],
    sections: [
      {
        h2: "Après la neige : l'eau qui passe sous la toiture",
        body: [
          "Dans l'Oesling, les hivers sont plus froids et plus enneigés que dans le reste du pays. Quand la neige fond sur le haut d'un toit chauffé par la maison et regèle au bord, au-dessus de la gouttière, il se forme un bourrelet de glace. L'eau de fonte s'accumule derrière et remonte sous les ardoises ou les tuiles. Une tache apparaît alors au plafond des combles ou de l'étage, seulement après un épisode de neige, et elle sèche entre deux.",
          "Comme ces infiltrations sont intermittentes, nous travaillons en deux temps. La [mesure d'humidité](/techniques/humidimetre) et la [caméra thermique](/techniques/camera-thermique) repèrent dans les combles l'isolant et les bois mouillés, ce qui délimite la zone. Nous vérifions ensuite que ce n'est pas une canalisation qui fuit, puis nous testons par temps sec les points faibles de la toiture : solin de cheminée, noue, fenêtre de toit, raccord de gouttière. Le rapport indique ce qu'il faut reprendre. Voir notre page [recherche d'infiltration](/recherche-infiltration).",
        ],
      },
      {
        h2: 'Chauffage au bois, aux pellets ou au mazout : la pression qui baisse',
        body: [
          "Beaucoup de maisons de la région se chauffent au mazout, aux pellets ou au bois, souvent avec un ballon tampon, parfois avec des panneaux solaires thermiques raccordés au même circuit. Plus l'installation compte de circuits et de raccords, plus il est difficile de savoir d'où vient une baisse de pression.",
          "Nous isolons les circuits un par un (radiateurs, plancher chauffant, ballon tampon, circuit solaire) et nous les testons par [contrôle de pression](/techniques/mise-en-pression) pour savoir lequel perd de l'eau. Un vase d'expansion dégonflé ou une soupape qui goutte donnent les mêmes symptômes : nous les vérifions aussi. Le point de fuite est ensuite localisé à la [caméra thermique](/techniques/camera-thermique) ou au [gaz traceur](/techniques/gaz-traceur). Voir notre page [fuite de chauffage](/recherche-fuite-chauffage).",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Wiltz',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: "J'ai une tache au plafond seulement après la neige : est-ce une fuite ?",
        a: "Probablement pas une fuite de canalisation. C'est souvent l'eau de fonte qui remonte sous la couverture, bloquée par un bourrelet de glace au bord du toit. Nous vérifions d'abord les canalisations, puis nous cherchons par où l'eau entre dans la toiture.",
      },
      {
        q: 'Peut-on chercher une infiltration de toiture en plein hiver ?',
        a: "Oui pour délimiter la zone touchée : la mesure d'humidité et la caméra thermique fonctionnent par tous les temps. Les tests de la toiture elle-même se font par temps sec et sans neige. En attendant, notez les dates des taches et photographiez-les.",
      },
      {
        q: 'Avez-vous des informations en allemand ?',
        a: "Oui, une partie du site est disponible en allemand, à partir de la page [Leckortung in Luxemburg](/de/leckortung-luxemburg).",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-infiltration', '/recherche-fuite-ettelbruck'],
  },
  {
    path: '/recherche-fuite-steinfort',
    parent: '/recherche-fuite-luxembourg',
    nav: 'Recherche de fuite à Steinfort',
    summary: 'Steinfort, Kleinbettingen, Grass et Hagen : la commune de nos bureaux. Conduites enterrées, pompes à chaleur.',
    title: 'Recherche de fuite à Steinfort, Kleinbettingen | Expomis',
    description: 'Recherche de fuite non destructive à Steinfort, Kleinbettingen, Grass et Hagen : conduite enterrée après le compteur, chauffage au sol. Rapport sous 72h.',
    eyebrow: 'Steinfort — Kleinbettingen — Grass — Hagen',
    h1: "Recherche de fuite d'eau à Steinfort",
    areaServed: [
      { '@type': 'City', name: 'Steinfort' },
      { '@type': 'City', name: 'Kleinbettingen' },
      { '@type': 'City', name: 'Grass' },
      { '@type': 'City', name: 'Hagen' },
    ],
    intro: [
      "Les bureaux d'Expomis sont à Steinfort, rue de Hobscheid. Nous localisons les fuites d'eau et les infiltrations sans démolition dans toute la commune, à Steinfort, Kleinbettingen, Grass et Hagen, comme dans les communes voisines : Habscht, Koerich, Garnich, [Mamer et Capellen](/recherche-fuite-mamer).",
      "Vous recevez sous 72h un rapport qui situe l'origine de la fuite, à transmettre à votre assureur ou à votre propriétaire.",
    ],
    sections: [
      {
        h2: 'Conduite enterrée entre le compteur et la maison',
        body: [
          "Dans les maisons avec cour, jardin ou longue allée, fréquentes à Grass, à Hagen et à Kleinbettingen, le compteur d'eau n'est pas toujours dans la maison : il peut se trouver dans un regard en limite de terrain, dans un garage ou une dépendance. Entre le compteur et la maison, la conduite passe alors sous la cour, l'allée ou la pelouse, parfois sur plusieurs dizaines de mètres.",
          "Une fuite sur ce tronçon ne laisse souvent aucune trace dans la maison. On la découvre par le compteur qui tourne, par une facture anormale, ou par une zone de pelouse qui reste verte et humide en plein été. Le [contrôle de pression](/techniques/mise-en-pression) confirme la fuite, puis le [gaz traceur](/techniques/gaz-traceur) ou l'[écoute acoustique](/techniques/detection-acoustique) indiquent où creuser : on ouvre un trou à l'endroit marqué plutôt que de refaire toute l'allée. Voir notre page [canalisation enterrée](/recherche-fuite-canalisation).",
          "Cette partie du réseau, située après le compteur, relève en général du propriétaire. Voir notre guide [fuite d'eau : qui paie ?](/fuite-eau-qui-paie)",
        ],
      },
      {
        h2: 'Pompe à chaleur et chauffage au sol qui perdent de la pression',
        body: [
          "Les maisons récentes de la commune sont souvent chauffées par une pompe à chaleur et un plancher chauffant. Quand la pression baisse et qu'il faut remettre de l'eau, tout n'est pas forcément une fuite dans la dalle : un vase d'expansion dégonflé ou une soupape de sécurité qui goutte produisent les mêmes symptômes. Nous vérifions ces organes en premier, puis nous testons les boucles une par une pour savoir laquelle perd de l'eau.",
          "Un plancher alimenté par une pompe à chaleur fonctionne à basse température : sur la [caméra thermique](/techniques/camera-thermique), la fuite ressort moins nettement que sur une chaudière classique. Nous faisons donc tourner le chauffage pendant l'intervention, et nous recoupons au [gaz traceur](/techniques/gaz-traceur) si l'image ne suffit pas. Voir notre page [fuite de chauffage au sol](/recherche-fuite-chauffage).",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Steinfort',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: [
      {
        q: 'La conduite entre mon compteur et ma maison fuit : qui paie la réparation ?',
        a: "En règle générale, tout ce qui se trouve après le compteur relève du propriétaire, et le branchement jusqu'au compteur du réseau public. La limite exacte est fixée par le règlement communal : renseignez-vous auprès du service des eaux de la commune. Voir notre guide [fuite d'eau : qui paie ?](/fuite-eau-qui-paie)",
      },
      {
        q: 'Ma pompe à chaleur perd de la pression : est-ce forcément une fuite ?',
        a: "Non. Un vase d'expansion dégonflé ou une soupape de sécurité qui goutte provoquent aussi une baisse de pression. Faites-les vérifier par votre chauffagiste ; si la pression baisse toujours, nous recherchons la fuite sur les boucles du plancher et les canalisations.",
      },
      {
        q: 'Vos bureaux sont à Steinfort : peut-on vous y rencontrer ?',
        a: "Nos bureaux sont au 69, rue de Hobscheid, à Steinfort. Pour un rendez-vous, appelez-nous ou passez par le [formulaire de contact](/contact).",
      },
      {
        q: 'Faut-il casser pour trouver la fuite ?',
        a: "Non. La localisation se fait sans démolition. Une ouverture reste nécessaire pour réparer, mais seulement à l'endroit que nous avons marqué.",
      },
      {
        q: 'Sous quel délai recevrai-je le rapport ?',
        a: 'Sous 72h. Il est illustré de photos et de mesures et peut être transmis tel quel à votre assureur.',
      },
      {
        q: 'Intervenez-vous en urgence ?',
        a: "Oui, un service d'urgence est joignable 24h/24 au +352 661 181 082.",
      },
    ],
    related: ['/recherche-fuite-luxembourg', '/recherche-fuite-mamer', '/recherche-fuite-arlon'],
  },
  {
    path: '/recherche-fuite-province-luxembourg',
    nav: 'Province de Luxembourg (Belgique)',
    summary: 'Recherche de fuite à Arlon, Aubange, Messancy, Virton et dans tout le sud de la province de Luxembourg.',
    title: 'Recherche de fuite province de Luxembourg (Belgique) | Expomis',
    description: 'Recherche de fuite non destructive dans le sud de la province de Luxembourg : Arlon, Aubange, Messancy, Virton, Attert, Habay. Rapport sous 72h.',
    eyebrow: 'Belgique — province de Luxembourg',
    h1: 'Recherche de fuite dans le sud de la province de Luxembourg',
    areaServed: AREAS.be,
    intro: [
      "Expomis intervient régulièrement dans le sud de la province de Luxembourg, en Belgique, pour localiser les fuites d'eau et les infiltrations sans démolition. Nos bureaux de Steinfort sont juste de l'autre côté de la frontière : Arlon, Aubange, Messancy, Virton et les communes voisines sont à courte distance.",
      "La méthode est la même qu'au Grand-Duché : localisation non destructive, marquage de la fuite sur place et rapport sous 72h, que vous pouvez transmettre à votre assureur belge.",
    ],
    sections: [
      {
        h2: 'Communes où nous intervenons',
        list: [
          '[Arlon](/recherche-fuite-arlon) et ses sections (Autelbas, Bonnert, Guirsch, Heinsch, Toernich)',
          '[Aubange](/recherche-fuite-aubange) : Athus, Aix-sur-Cloie, Battincourt, Halanzy, Rachecourt',
          '[Messancy](/recherche-fuite-messancy) : Habergy, Hondelange, Sélange, Wolkrange',
          '[Virton](/recherche-fuite-virton) : Bleid, Ethe, Latour, Ruette, Saint-Mard',
          'Attert, Habay, Étalle, Musson, Saint-Léger, Rouvroy, Meix-devant-Virton, Tintigny',
        ],
        after: ["Votre commune n'est pas dans la liste ? Appelez-nous, nous vous dirons si nous pouvons intervenir."],
      },
      {
        h2: 'Les fuites et infiltrations que nous localisons',
        list: TYPES_DE_FUITE,
      },
      {
        h2: 'Maisons de la région : ce que nous rencontrons',
        body: [
          "Le sud de la province compte beaucoup de maisons anciennes aux murs de pierre épais, notamment en Gaume. Dans ces murs, une tache d'humidité peut venir d'une fuite, d'une infiltration ou de remontées capillaires, et chaque cause appelle une réparation différente. La [mesure d'humidité](/techniques/humidimetre) sert d'abord à trancher entre ces hypothèses.",
          "Dans les constructions plus récentes, les canalisations en multicouche et le [chauffage au sol](/recherche-fuite-chauffage) sont courants : la fuite est noyée dans la chape, et la [caméra thermique](/techniques/camera-thermique) ou le [gaz traceur](/techniques/gaz-traceur) évitent d'ouvrir le sol au hasard.",
        ],
      },
      {
        h2: 'Assurance : ce qu’il faut savoir en Belgique',
        body: [
          "Beaucoup de contrats d'assurance habitation belges prévoient la prise en charge des frais de recherche de fuite lorsqu'il y a un dégât des eaux, souvent dans la limite d'un plafond. Vérifiez vos conditions générales ou interrogez votre courtier.",
          "Notre [rapport](/rapport-recherche-fuite) décrit les constats, les techniques utilisées, l'origine de la fuite et les dommages observés. Il est rédigé en français.",
        ],
      },
    ],
    faq: [
      {
        q: 'Une société luxembourgeoise peut-elle intervenir chez moi en Belgique ?',
        a: 'Oui. Nous intervenons régulièrement dans le sud de la province de Luxembourg, depuis nos bureaux de Steinfort, à quelques kilomètres de la frontière.',
      },
      ...cityFaq('Arlon ou Virton').slice(1),
    ],
    related: ['/recherche-fuite-luxembourg', '/techniques', '/rapport-recherche-fuite'],
  },
  {
    path: '/recherche-fuite-arlon',
    parent: '/recherche-fuite-province-luxembourg',
    nav: 'Recherche de fuite à Arlon',
    summary: 'Arlon et ses sections, à quelques kilomètres de nos bureaux de Steinfort.',
    title: 'Recherche de fuite à Arlon | Détection non destructive | Expomis',
    description: "Recherche de fuite d'eau non destructive à Arlon, Autelbas, Bonnert, Guirsch, Heinsch et Toernich. Localisation précise, rapport pour l'assurance sous 72h.",
    eyebrow: 'Arlon — province de Luxembourg',
    h1: "Recherche de fuite d'eau à Arlon",
    areaServed: [{ '@type': 'City', name: 'Arlon' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations à Arlon et dans ses sections (Autelbas, Bonnert, Guirsch, Heinsch, Toernich) sans démolition. Nos bureaux de Steinfort sont à quelques kilomètres, de l'autre côté de la frontière.",
      'Nous intervenons aussi dans les communes voisines : Attert, Habay et [Messancy](/recherche-fuite-messancy).',
    ],
    sections: [
      {
        h2: 'Le bâti arlonais et ses fuites',
        body: [
          "Arlon mêle maisons anciennes du centre, maisons de ville mitoyennes et quartiers résidentiels plus récents. Dans les maisons mitoyennes et les immeubles à appartements, une fuite se remarque souvent chez le voisin avant d'être visible chez soi. La première question est alors de savoir de quel logement elle provient : c'est ce que les tests par circuit permettent d'établir.",
          "Dans les constructions récentes, le [chauffage au sol](/recherche-fuite-chauffage) et les canalisations noyées dans la chape sont fréquents. Une fuite y fait baisser la pression de la chaudière sans laisser de trace visible, jusqu'à ce que le revêtement se décolle.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Arlon',
        list: TYPES_DE_FUITE,
      },
      {
        h2: 'Nos méthodes',
        body: [
          "Nous commençons par délimiter la zone humide et par tester les circuits en pression pour savoir lequel fuit. Nous localisons ensuite le point de fuite avec la technique adaptée : [caméra thermique](/techniques/camera-thermique), [écoute acoustique](/techniques/detection-acoustique), [gaz traceur](/techniques/gaz-traceur), ou [fluorescéine](/techniques/fluoresceine) pour les évacuations. L'emplacement est marqué sur place et vous recevez le rapport sous 72h.",
        ],
      },
    ],
    faq: cityFaq('Arlon'),
    related: ['/recherche-fuite-province-luxembourg', '/recherche-fuite-messancy', '/recherche-fuite-aubange'],
  },
  {
    path: '/recherche-fuite-aubange',
    parent: '/recherche-fuite-province-luxembourg',
    nav: 'Recherche de fuite à Aubange',
    summary: 'Aubange, Athus, Halanzy et les villages de la commune, à la frontière du Grand-Duché.',
    title: 'Recherche de fuite à Aubange et Athus | Expomis',
    description: "Recherche de fuite d'eau non destructive à Aubange, Athus, Aix-sur-Cloie, Battincourt, Halanzy et Rachecourt. Rapport pour l'assurance sous 72h.",
    eyebrow: 'Aubange — province de Luxembourg',
    h1: "Recherche de fuite d'eau à Aubange et Athus",
    areaServed: [{ '@type': 'City', name: 'Aubange' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations dans toute la commune d'Aubange (Athus, Aix-sur-Cloie, Aubange, Battincourt, Halanzy, Rachecourt) sans démolition. La commune touche le Grand-Duché : nous y intervenons régulièrement depuis nos bureaux de Steinfort.",
      'Nous intervenons aussi dans les communes voisines de [Messancy](/recherche-fuite-messancy) et Musson.',
    ],
    sections: [
      {
        h2: 'Maisons anciennes : les raccords, points faibles fréquents',
        body: [
          "Athus et Aubange comptent de nombreuses maisons anciennes, dont l'installation a été modifiée au fil des rénovations. On y trouve encore des canalisations en acier galvanisé, parfois en plomb, raccordées à des tronçons récents en cuivre ou en multicouche. Ces raccords entre matériaux différents, et les canalisations anciennes corrodées de l'intérieur, sont des origines de fuite fréquentes.",
          "Dans ces maisons, les caves humides posent aussi souvent la question : fuite, [infiltration](/recherche-infiltration) ou remontées capillaires ? La [mesure d'humidité](/techniques/humidimetre) permet de trancher avant d'engager des travaux.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Aubange',
        list: TYPES_DE_FUITE,
      },
      {
        h2: 'Nos méthodes',
        body: [
          "Contrôle de pression pour identifier le circuit en cause, puis localisation précise : [détection acoustique](/techniques/detection-acoustique) et [gaz traceur](/techniques/gaz-traceur) pour les canalisations d'eau, [test fumigène](/techniques/test-fumigene) et [caméra d'inspection](/techniques/camera-canalisation) pour les évacuations et les égouts. Rapport sous 72h.",
        ],
      },
    ],
    faq: cityFaq('Aubange'),
    related: ['/recherche-fuite-province-luxembourg', '/recherche-fuite-arlon', '/recherche-fuite-messancy'],
  },
  {
    path: '/recherche-fuite-messancy',
    parent: '/recherche-fuite-province-luxembourg',
    nav: 'Recherche de fuite à Messancy',
    summary: 'Messancy, Habergy, Hondelange, Sélange et Wolkrange.',
    title: 'Recherche de fuite à Messancy | Sans casse | Expomis',
    description: "Recherche de fuite d'eau non destructive à Messancy, Habergy, Hondelange, Sélange et Wolkrange : chauffage au sol, canalisations enterrées. Rapport sous 72h.",
    eyebrow: 'Messancy — province de Luxembourg',
    h1: "Recherche de fuite d'eau à Messancy",
    areaServed: [{ '@type': 'City', name: 'Messancy' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations dans la commune de Messancy (Habergy, Hondelange, Messancy, Sélange, Wolkrange) sans démolition, entre [Arlon](/recherche-fuite-arlon) et [Aubange](/recherche-fuite-aubange).",
    ],
    sections: [
      {
        h2: 'Maisons individuelles : chauffage au sol et conduites enterrées',
        body: [
          "Messancy et ses villages comptent beaucoup de maisons individuelles, dont de nombreuses constructions récentes. Deux situations y reviennent souvent.",
        ],
        list: [
          "Le [chauffage au sol](/recherche-fuite-chauffage) qui perd de la pression : les tubes sont noyés dans la chape, la fuite ne se voit pas. Nous testons chaque boucle au collecteur, puis la [caméra thermique](/techniques/camera-thermique) fait apparaître le point de fuite.",
          "La conduite enterrée qui alimente la maison, un garage ou un robinet extérieur : elle peut courir sur plusieurs dizaines de mètres sous le jardin. Le [gaz traceur](/techniques/gaz-traceur) et l'[écoute acoustique](/techniques/detection-acoustique) indiquent où creuser.",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Messancy',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: cityFaq('Messancy'),
    related: ['/recherche-fuite-province-luxembourg', '/recherche-fuite-arlon', '/recherche-fuite-aubange'],
  },
  {
    path: '/recherche-fuite-virton',
    parent: '/recherche-fuite-province-luxembourg',
    nav: 'Recherche de fuite à Virton',
    summary: 'Virton et la Gaume : Bleid, Ethe, Latour, Ruette, Saint-Mard.',
    title: 'Recherche de fuite à Virton et en Gaume | Expomis',
    description: "Recherche de fuite et d'humidité non destructive à Virton, Ethe, Saint-Mard, Bleid, Latour, Ruette et en Gaume. Rapport pour l'assurance sous 72h.",
    eyebrow: 'Virton — province de Luxembourg',
    h1: "Recherche de fuite d'eau à Virton",
    areaServed: [{ '@type': 'City', name: 'Virton' }],
    intro: [
      "Expomis localise les fuites d'eau et les infiltrations à Virton et dans ses sections (Bleid, Ethe, Latour, Ruette, Saint-Mard) sans démolition, ainsi que dans les communes gaumaises voisines : Meix-devant-Virton, Rouvroy, Saint-Léger, Musson, Étalle et Tintigny.",
    ],
    sections: [
      {
        h2: "Maisons en pierre : fuite, infiltration ou remontée d'humidité ?",
        body: [
          "Les maisons anciennes de Gaume ont des murs de pierre épais qui absorbent et restituent l'humidité. Une tache sur un mur peut y avoir trois origines très différentes : une canalisation qui fuit, une infiltration par la façade ou la toiture, ou des remontées capillaires depuis le sol. Chacune appelle une réparation différente, et se tromper coûte cher.",
          "Nous commençons donc par une cartographie de l'humidité à l'[humidimètre](/techniques/humidimetre) : la forme et le gradient de la zone humide indiquent de quel côté vient l'eau. Nous testons ensuite les canalisations en pression. Si elles sont étanches, nous recherchons l'[infiltration](/recherche-infiltration) par des mises en eau ciblées à la [fluorescéine](/techniques/fluoresceine).",
        ],
      },
      {
        h2: 'Ce que nous recherchons à Virton',
        list: TYPES_DE_FUITE,
      },
    ],
    faq: cityFaq('Virton'),
    related: ['/recherche-fuite-province-luxembourg', '/recherche-infiltration', '/techniques/humidimetre'],
  },
];
